import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  LinearProgress,
  CircularProgress,
  IconButton,
  Link
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  LocalAtm as LocalAtmIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  ArrowUpward as ArrowUpwardIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import Navbar from './Navbar';
import { API_URL } from '../config';

const Referrals = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  
  // State variables
  const [referralCode, setReferralCode] = useState('');
  const [referralUrl, setReferralUrl] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectAccountStatus, setConnectAccountStatus] = useState(null);
  
  // Dialog states
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutType, setPayoutType] = useState('credit');
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Minimum payout threshold
  const MINIMUM_PAYOUT = 50;
  
  // Load referral data on component mount
  useEffect(() => {
    fetchReferralCode();
    fetchReferrals();
    checkConnectAccountStatus();
  }, []);
  
  // Fetch user's referral code
  const fetchReferralCode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/referral/referral-code`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log("Referral code response:", data);
        if (data.referral_code && data.referral_code.code) {
          const code = data.referral_code.code;
          setReferralCode(code);
          // Use the dedicated referral landing page URL with the referral code
          setReferralUrl(`${window.location.origin}/referral?ref=${code}`);
        } else {
          console.error('Invalid referral code response format:', data);
        }
      } else {
        console.error('Failed to fetch referral code:', await response.text());
        showSnackbar('Error fetching your referral code. Please try again later.', 'error');
      }
    } catch (error) {
      console.error('Error fetching referral code:', error);
      showSnackbar('Network error. Please try again later.', 'error');
    }
  };
  
  // Fetch user's referrals and earnings
  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/referral/referrals`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log("Referrals data:", data);
        setReferrals(data.referrals || []);
        setTotalEarnings(data.total_earnings || 0);
        setAvailableBalance(data.available_balance || 0);
        setPayoutHistory(data.payout_history || []);
      } else {
        console.error('Failed to fetch referrals:', await response.text());
        showSnackbar('Error fetching your referrals. Please try again later.', 'error');
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
      showSnackbar('Network error. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Check Stripe Connect account status
  const checkConnectAccountStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/referral/stripe-connect/account-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConnectAccountStatus(data);
      } else {
        console.error('Failed to fetch Connect account status:', await response.text());
      }
    } catch (error) {
      console.error('Error fetching Connect account status:', error);
    }
  };
  
  // Copy referral link to clipboard
  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralUrl);
    showSnackbar('Referral link copied to clipboard', 'success');
  };
  
  // Share referral link on social media
  const shareOnSocial = (platform) => {
    let url;
    const text = 'Join me on this amazing platform and we both get rewards!';
    
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}&quote=${encodeURIComponent(text)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(referralUrl)}&text=${encodeURIComponent(text)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(referralUrl)}&title=${encodeURIComponent(text)}`;
        break;
      case 'email':
        url = `mailto:?subject=${encodeURIComponent('Check out this platform')}&body=${encodeURIComponent(`${text} ${referralUrl}`)}`;
        break;
      default:
        return;
    }
    
    window.open(url, '_blank');
  };
  
  // Handle payout request
  const handleRequestPayout = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/referral/request-payout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          payout_type: payoutType
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        showSnackbar(data.message, 'success');
        fetchReferrals(); // Refresh data
        setPayoutDialogOpen(false);
      } else {
        showSnackbar(data.message || 'Failed to process payout', 'error');
      }
    } catch (error) {
      console.error('Error processing payout:', error);
      showSnackbar('Error processing payout', 'error');
    }
  };
  
  // Create Stripe Connect account
  const createConnectAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/referral/stripe-connect/create-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Open the Stripe onboarding URL in a new tab
        window.open(data.onboarding_url, '_blank');
        setConnectDialogOpen(false);
        showSnackbar('Stripe account creation initiated', 'success');
        
        // Check status after a delay to allow the user to complete onboarding
        setTimeout(() => {
          checkConnectAccountStatus();
        }, 5000);
      } else {
        showSnackbar(data.message || 'Failed to create Stripe account', 'error');
      }
    } catch (error) {
      console.error('Error creating Stripe Connect account:', error);
      showSnackbar('Error creating Stripe Connect account', 'error');
    }
  };
  
  // Show snackbar message
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ pt: 4, pb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Referral Program
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Earn 20% of the profit from users you refer to our platform
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 4 }} />
      
      {/* Referral Link Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Your Referral Link
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            value={referralUrl}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mr: 1 }}
          />
          <Tooltip title="Copy to clipboard">
            <Button
              variant="contained"
              color="primary"
              onClick={copyReferralLink}
              startIcon={<ContentCopyIcon />}
            >
              Copy
            </Button>
          </Tooltip>
        </Box>
        
        <Typography variant="body2" gutterBottom>
          Share your link on social media:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Tooltip title="Share on Facebook">
            <IconButton color="primary" onClick={() => shareOnSocial('facebook')}>
              <FacebookIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share on Twitter">
            <IconButton color="primary" onClick={() => shareOnSocial('twitter')}>
              <TwitterIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share on LinkedIn">
            <IconButton color="primary" onClick={() => shareOnSocial('linkedin')}>
              <LinkedInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share via Email">
            <IconButton color="primary" onClick={() => shareOnSocial('email')}>
              <EmailIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            When someone signs up using your referral link, you'll earn 20% of their payments. Your referral code: <strong>{referralCode}</strong>
          </Typography>
        </Alert>
      </Paper>
      
      {/* Stats Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon color="primary" sx={{ fontSize: 48, mr: 2 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Referrals
                  </Typography>
                  <Typography variant="h4">
                    {loading ? <CircularProgress size={24} /> : referrals.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocalAtmIcon color="success" sx={{ fontSize: 48, mr: 2 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Earnings
                  </Typography>
                  <Typography variant="h4">
                    {loading ? <CircularProgress size={24} /> : formatCurrency(totalEarnings)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountBalanceIcon color="info" sx={{ fontSize: 48, mr: 2 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Available for Withdrawal
                  </Typography>
                  <Typography variant="h4">
                    {loading ? <CircularProgress size={24} /> : formatCurrency(availableBalance)}
                  </Typography>
                </Box>
              </Box>
              
              {availableBalance > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    startIcon={<ArrowUpwardIcon />}
                    onClick={() => setPayoutDialogOpen(true)}
                    disabled={availableBalance < MINIMUM_PAYOUT}
                  >
                    {availableBalance < MINIMUM_PAYOUT
                      ? `Need ${formatCurrency(MINIMUM_PAYOUT - availableBalance)} more to withdraw`
                      : 'Withdraw Earnings'}
                  </Button>
                  
                  {availableBalance < MINIMUM_PAYOUT && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(availableBalance / MINIMUM_PAYOUT) * 100}
                        color="success"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {`${availableBalance}/${MINIMUM_PAYOUT} minimum threshold`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Referrals Table */}
      <Paper elevation={2} sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Your Referrals
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : referrals.length === 0 ? (
          <Alert severity="info" sx={{ my: 2 }}>
            You haven't referred anyone yet. Share your referral link to start earning!
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Date Joined</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Earnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referrals.map((referral) => (
                  <TableRow key={referral.referral.id} hover>
                    <TableCell>
                      {referral.user.email}
                    </TableCell>
                    <TableCell>
                      {formatDate(referral.date_joined)}
                    </TableCell>
                    <TableCell>
                      {referral.referral.status === 'active' ? (
                        <Alert severity="success" icon={false} sx={{ py: 0 }}>
                          Active
                        </Alert>
                      ) : (
                        <Alert severity="warning" icon={false} sx={{ py: 0 }}>
                          Pending
                        </Alert>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(referral.total_earned)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      {/* Payout History */}
      <Paper elevation={2} sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Payout History
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : payoutHistory.length === 0 ? (
          <Alert severity="info" sx={{ my: 2 }}>
            You haven't made any withdrawals yet.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payoutHistory.map((payout) => (
                  <TableRow key={payout.id} hover>
                    <TableCell>
                      {formatDate(payout.created_at)}
                    </TableCell>
                    <TableCell>
                      {payout.payout_type === 'credit' ? 'Platform Credits' : 'Bank Transfer'}
                    </TableCell>
                    <TableCell>
                      {payout.status === 'completed' ? (
                        <Alert severity="success" icon={false} sx={{ py: 0 }}>
                          Completed
                        </Alert>
                      ) : payout.status === 'processing' ? (
                        <Alert severity="warning" icon={false} sx={{ py: 0 }}>
                          Processing
                        </Alert>
                      ) : (
                        <Alert severity="error" icon={false} sx={{ py: 0 }}>
                          Failed
                        </Alert>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(payout.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      {/* How It Works Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          How It Works
        </Typography>
        <Box component="ol" sx={{ pl: 2 }}>
          <Typography component="li" sx={{ mb: 1 }}>
            Share your unique referral link with friends, colleagues, or on social media.
          </Typography>
          <Typography component="li" sx={{ mb: 1 }}>
            When someone signs up using your link, they're tagged as your referral.
          </Typography>
          <Typography component="li" sx={{ mb: 1 }}>
            You'll earn 20% of the platform profit from every purchase they make.
          </Typography>
          <Typography component="li" sx={{ mb: 1 }}>
            Once you reach the ${MINIMUM_PAYOUT} minimum threshold, you can withdraw your earnings as platform credits or direct bank transfer.
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Note: Referrals must make at least one purchase to be considered active. Earnings can be withdrawn once they reach the ${MINIMUM_PAYOUT} minimum threshold.
          </Typography>
        </Alert>
      </Paper>
      
      {/* Payout Dialog */}
      <Dialog open={payoutDialogOpen} onClose={() => setPayoutDialogOpen(false)}>
        <DialogTitle>Withdraw Earnings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            You have {formatCurrency(availableBalance)} available for withdrawal.
          </Typography>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="payout-type-label">Payout Method</InputLabel>
            <Select
              labelId="payout-type-label"
              value={payoutType}
              onChange={(e) => setPayoutType(e.target.value)}
              label="Payout Method"
            >
              <MenuItem value="credit">Platform Credits</MenuItem>
              <MenuItem value="stripe">Bank Transfer (Stripe)</MenuItem>
            </Select>
          </FormControl>
          
          {payoutType === 'stripe' && (
            <>
              {!connectAccountStatus?.has_account && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  You need to set up a Stripe account first to receive direct bank transfers. 
                  <Button 
                    color="inherit" 
                    size="small" 
                    sx={{ ml: 1 }}
                    onClick={() => {
                      setPayoutDialogOpen(false);
                      setConnectDialogOpen(true);
                    }}
                  >
                    Set up now
                  </Button>
                </Alert>
              )}
              
              {connectAccountStatus?.has_account && !connectAccountStatus?.payouts_enabled && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Your Stripe account setup is incomplete. Please complete your account verification.
                  <Button 
                    color="inherit" 
                    size="small" 
                    sx={{ ml: 1 }}
                    onClick={createConnectAccount}
                  >
                    Complete setup
                  </Button>
                </Alert>
              )}
            </>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {payoutType === 'credit' ? 
              'Your earnings will be added to your account as credits immediately.' : 
              'Your earnings will be transferred to your bank account. Processing may take 3-5 business days.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayoutDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleRequestPayout}
            disabled={
              payoutType === 'stripe' && 
              (!connectAccountStatus?.has_account || !connectAccountStatus?.payouts_enabled)
            }
          >
            Confirm Withdrawal
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Stripe Connect Dialog */}
      <Dialog open={connectDialogOpen} onClose={() => setConnectDialogOpen(false)}>
        <DialogTitle>Set Up Bank Transfers</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            To receive direct bank transfers, you need to set up a Stripe Connect account. This is a secure way to receive payments directly to your bank account.
          </Typography>
          <Typography variant="body2" paragraph>
            The setup process will:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2">
              Ask for your banking information
            </Typography>
            <Typography component="li" variant="body2">
              Verify your identity (KYC requirements)
            </Typography>
            <Typography component="li" variant="body2">
              Set up your payout method
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Your information is securely handled by Stripe, a leading payment processor.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={createConnectAccount}>
            Set Up Stripe Account
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Referrals; 